import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

import {
  ProfessionalTitle,
  professionalTitleMapping,
} from 'src/app/core/enums/professional-title';
import { Characteristic } from 'src/app/core/entities/characteristic.entity';
import { ErrorService } from 'src/app/core/services/error.service';
import { Profile } from 'src/app/core/entities/profile.entity';
import { ProfileFormHandler } from 'src/app/core/form-handler/profile.form-handler';
import { ProfileService } from 'src/app/core/services/profile.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { professionalSpecialityMapping } from 'src/app/core/enums/professional-speciality';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
})
@AutoUnsub()
export class ProfileComponent implements OnInit {
  @Input() settings = false;
  @Input() profile: Profile = new Profile();
  @Output() updatedProfile = new EventEmitter<FormGroup>();
  private _observers: Subscription = new Subscription();
  public profileFormHandler: ProfileFormHandler = new ProfileFormHandler(
    ProfileFormHandler.PROFILE_FORM_STEP_PERSONAL_INFORMATIONS
  );
  public profileCreationForm: FormGroup;
  public professionalTitle = ProfessionalTitle;
  public professionalTitleMapping = professionalTitleMapping;
  public professionalSpecialityMapping = professionalSpecialityMapping;
  public submittedProfileCreationForm = false;
  public showId = false;

  constructor(
    private profileService: ProfileService,
    private spinnerService: SpinnerService,
    private toastrService: ToastrService,
    private errorService: ErrorService
  ) {
    this.prepareCreationForm();
  }

  ngOnInit(): void {
    this.prepareCreationForm();
    this.setObservers();
  }

  /**
   * Prepares form group
   */
  private prepareCreationForm(): void {
    this.profileCreationForm = this.profileFormHandler.init();
    this.profileFormHandler.update(this.profile);
  }

  /**
   * Sets observers
   */
  private setObservers(): void {
    if (this.settings) {
      this._observers.add(this.onProfileDataChanged());
    }
  }

  /**
   * Changes profile data
   * @returns Subscription
   */
  private onProfileDataChanged(): Subscription {
    this.submittedProfileCreationForm = true;
    return this.profileFormHandler.dataChanged.subscribe((res) => {
      let data = {};

      res.forEach((profileData) => {
        if (
          profileData.formControl.valid &&
          this.profile[profileData.key] !== profileData.formControl.value
        ) {
          data[profileData.key] = profileData.formControl.value;
        }
      });

      if (Object.entries(data).length > 0) {
        this.updateProfile(data);
      }
    });
  }

  /**
   * Submits form
   */
  public onSubmit(): void {
    this.submittedProfileCreationForm = true;
    if (this.profileCreationForm.valid) {
      this.updatedProfile.emit(this.profileCreationForm);
    }
  }

  /**
   * Updates profile
   * @param data
   */
  private updateProfile(data: any): void {
    this.spinnerService.show();

    this.profileService
      .updateCharacteristics(data)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res) => {
          Object.entries(res).forEach(([key, value]) => {
            this.profile.updateCharacteristic(value as Characteristic);
          });

          // Update service profile
          this.profileService.me = this.profile;

          this.toastrService.success(
            $localize`:@@settings_editAccount_ProfileSuccess:Your profile is well completed`
          );
        },
        error: (err) => {
          this.errorService.addError(
            $localize`:@@settings_editAccount_ErrorDescription:A problem is occured, please retry later`
          );
          this.errorService.show();
        },
      });
  }
}
